import React, { useState, useEffect, useCallback } from 'react';
import { fetchBankSurveys, deleteBankSurvey } from '../../api/client';
import formatDate from '../../utils/formatDate';
import './BankSurveyList.css';

const QUESTION_META = {
  overall_satisfaction: { label: 'Overall Satisfaction', type: 'rating', max: 5 },
  service_quality: { label: 'Service Quality', type: 'text' },
  product_suggestion: { label: 'Product/Service Suggestion', type: 'text' },
  likelihood_to_recommend: { label: 'Likelihood to Recommend', type: 'rating', max: 10 },
};

function getRatingBadge(value, max) {
  const ratio = value / max;
  if (ratio >= 0.8) return 'badge-positive';
  if (ratio >= 0.5) return 'badge-pending';
  return 'badge-negative';
}

function getOutcomeBadge(outcome) {
  if (outcome === 'completed') return 'badge-positive';
  if (outcome === 'partially_completed') return 'badge-pending';
  return 'badge-negative';
}

export default function BankSurveyList() {
  const [surveys, setSurveys] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [expandedId, setExpandedId] = useState(null);
  const [confirmId, setConfirmId] = useState(null);
  const [skip, setSkip] = useState(0);
  const limit = 20;

  const loadSurveys = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await fetchBankSurveys({ limit, skip });
      setSurveys(res.data || []);
      setTotal(res.meta?.total || 0);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [skip]);

  useEffect(() => {
    loadSurveys();
  }, [loadSurveys]);

  const toggleExpand = (id) => {
    setExpandedId((prev) => (prev === id ? null : id));
  };

  const handleDelete = async (e, id) => {
    e.stopPropagation();
    if (confirmId === id) {
      try {
        await deleteBankSurvey(id);
        setConfirmId(null);
        loadSurveys();
      } catch (err) {
        setError(err.message);
      }
    } else {
      setConfirmId(id);
    }
  };

  const handleCancelDelete = (e) => {
    e.stopPropagation();
    setConfirmId(null);
  };

  if (loading && surveys.length === 0) {
    return <div className="loading">Loading bank surveys...</div>;
  }

  if (error) {
    return <div className="error-message">Failed to load bank surveys: {error}</div>;
  }

  if (surveys.length === 0) {
    return <div className="card"><p>No bank survey responses yet.</p></div>;
  }

  const hasMore = skip + limit < total;
  const hasPrev = skip > 0;

  return (
    <div className="card">
      <h3>Survey Responses</h3>
      <table>
        <thead>
          <tr>
            <th>Date</th>
            <th>Phone</th>
            <th>Satisfaction</th>
            <th>NPS</th>
            <th>Outcome</th>
            <th></th>
          </tr>
        </thead>
        <tbody>
          {surveys.map((s) => (
            <React.Fragment key={s._id}>
              <tr onClick={() => toggleExpand(s._id)} style={{ cursor: 'pointer' }}>
                <td>{formatDate(s.created_at)}</td>
                <td style={{ fontFamily: 'monospace', fontSize: '0.85rem' }}>
                  {s.phone_number || 'N/A'}
                </td>
                <td>
                  {s.satisfaction_score != null ? (
                    <span className={`badge ${getRatingBadge(s.satisfaction_score, 5)}`}>
                      {s.satisfaction_score} / 5
                    </span>
                  ) : '\u2014'}
                </td>
                <td>
                  {s.nps_score != null ? (
                    <span className={`badge ${getRatingBadge(s.nps_score, 10)}`}>
                      {s.nps_score} / 10
                    </span>
                  ) : '\u2014'}
                </td>
                <td>
                  <span className={`badge ${getOutcomeBadge(s.call_outcome)}`}>
                    {(s.call_outcome || 'unknown').replace('_', ' ')}
                  </span>
                </td>
                <td onClick={(e) => e.stopPropagation()} style={{ whiteSpace: 'nowrap' }}>
                  {confirmId === s._id ? (
                    <>
                      <button className="btn-danger-sm" onClick={(e) => handleDelete(e, s._id)}>Confirm</button>
                      {' '}
                      <button className="btn-secondary-sm" onClick={handleCancelDelete}>Cancel</button>
                    </>
                  ) : (
                    <button className="btn-icon" onClick={(e) => handleDelete(e, s._id)} title="Delete">&#128465;</button>
                  )}
                </td>
              </tr>
              {expandedId === s._id && (
                <tr className="bank-survey-detail-row">
                  <td colSpan={6}>
                    <div className="bank-survey-details">
                      <div className="bank-survey-meta">
                        <span className="detail-label">Call ID:</span>{' '}
                        <span className="detail-value">{s.call_id || 'N/A'}</span>
                      </div>
                      <div className="bank-survey-responses-grid">
                        {Object.entries(QUESTION_META).map(([key, meta]) => {
                          const resp = (s.responses || {})[key];
                          if (!resp) return null;
                          return (
                            <div key={key} className="bank-survey-response-item">
                              <div className="bank-survey-response-header">
                                <span className="bank-survey-question-label">{meta.label}</span>
                                {meta.type === 'rating' && (
                                  <span className={`badge ${getRatingBadge(resp.value, meta.max)}`}>
                                    {resp.value} / {meta.max}
                                  </span>
                                )}
                              </div>
                              {meta.type === 'text' && (
                                <div className="bank-survey-text-response">
                                  {resp.value || 'No response'}
                                </div>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  </td>
                </tr>
              )}
            </React.Fragment>
          ))}
        </tbody>
      </table>
      {(hasPrev || hasMore) && (
        <div className="submission-pagination">
          <button
            className="btn-secondary"
            disabled={!hasPrev}
            onClick={() => setSkip((prev) => Math.max(0, prev - limit))}
          >
            Previous
          </button>
          <span className="pagination-info">
            {skip + 1}–{Math.min(skip + limit, total)} of {total}
          </span>
          <button
            className="btn-secondary"
            disabled={!hasMore}
            onClick={() => setSkip((prev) => prev + limit)}
          >
            Next
          </button>
        </div>
      )}
    </div>
  );
}
